import { z } from 'zod';
import { reportingDefinitionValueSchema, reportingFinanceProjectPerformanceCapability, reportingFinanceProjectPerformanceDefinitionValueSchema, reportingTrainingTeamSummaryDefinitionValueSchema, } from './reporting-definition.contract.js';
import { reportingTrainingTeamSummaryRowSchema, reportingTrainingTeamSummaryTotalsSchema, } from './reporting-training-team-summary.contract.js';
export const reportingFinanceProjectFinancialsSlot = 'reporting.finance-project-financials';
export const reportingSourceUnavailableReasonSchema = z.enum([
    'not-installed', 'not-enabled', 'permission-denied', 'provider-invalid', 'provider-failed',
]);
export const reportingExecutionUnavailableReasonSchema = z.enum([
    ...reportingSourceUnavailableReasonSchema.options,
    'definition-not-active', 'configuration-invalid',
]);
const centsSchema = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER);
const signedCentsSchema = z.number().int().min(Number.MIN_SAFE_INTEGER).max(Number.MAX_SAFE_INTEGER);
export const reportingFinanceProjectFinancialsRequestSchema = z.strictObject({
    consumerModuleId: z.literal('reporting'),
    requestedCapability: z.literal(reportingFinanceProjectPerformanceCapability),
    resourceType: z.literal('reporting.dataset'),
    resourceId: z.string().uuid(),
    projectIds: z.array(z.string().uuid()).min(1).max(500),
    asOf: z.string().datetime({ offset: true }),
});
export const reportingFinanceProjectFinancialFactSchema = z.strictObject({
    projectId: z.string().uuid(), currencyCode: z.literal('USD'),
    invoiceCount: z.number().int().nonnegative(), invoicedCents: centsSchema,
    collectedCents: centsSchema, writtenOffCents: centsSchema, outstandingCents: centsSchema,
    approvedDirectExpenseCount: z.number().int().nonnegative(), approvedDirectExpenseCents: centsSchema,
    referralCommissionCount: z.number().int().nonnegative(), referralCommissionCents: centsSchema,
    overheadCents: centsSchema, knownFinanceCostCents: centsSchema,
    knownFinanceContributionCents: signedCentsSchema,
}).superRefine((fact, context) => {
    if (fact.invoicedCents - fact.collectedCents - fact.writtenOffCents !== fact.outstandingCents) {
        context.addIssue({ code: 'custom', path: ['outstandingCents'], message: 'Invoice money must reconcile exactly.' });
    }
    const knownCost = fact.approvedDirectExpenseCents + fact.referralCommissionCents + fact.overheadCents;
    if (fact.knownFinanceCostCents !== knownCost ||
        fact.knownFinanceContributionCents !== fact.invoicedCents - knownCost) {
        context.addIssue({ code: 'custom', path: ['knownFinanceContributionCents'], message: 'Finance contribution must reconcile exactly.' });
    }
});
export const reportingFinanceProjectFinancialsResponseSchema = z.strictObject({
    state: z.literal('present'), capability: z.literal('finance.reporting-project-financials.v1'),
    tenantId: z.string().uuid(), asOf: z.string().datetime({ offset: true }),
    projects: z.array(reportingFinanceProjectFinancialFactSchema).min(1).max(500),
    contentHash: z.string().regex(/^[a-f0-9]{64}$/),
});
export const reportingBuiltInDefinitionKeySchema = z.enum([
    'finance-project-performance', 'training-team-summary',
]);
export const reportingExecutionSourceSchema = z.discriminatedUnion('kind', [
    z.strictObject({ kind: z.literal('built-in'), key: reportingBuiltInDefinitionKeySchema }),
    z.strictObject({ kind: z.literal('tenant-definition'), definitionId: z.string().uuid() }),
    z.strictObject({
        kind: z.literal('tenant-definition-version'), definitionId: z.string().uuid(),
        versionId: z.string().uuid(),
    }),
]);
export const reportingExecutionRequestSchema = z.strictObject({
    executionId: z.string().uuid(), source: reportingExecutionSourceSchema,
});
export const reportingFinanceProjectPerformanceRowSchema = z.strictObject({
    projectId: z.string().uuid(), projectNumber: z.string(), projectName: z.string(),
    projectStatus: z.string(), invoiceCount: z.number().int().nonnegative(),
    invoicedCents: centsSchema, collectedCents: centsSchema, writtenOffCents: centsSchema,
    outstandingCents: centsSchema, approvedDirectExpenseCount: z.number().int().nonnegative(),
    approvedDirectExpenseCents: centsSchema, referralCommissionCount: z.number().int().nonnegative(),
    referralCommissionCents: centsSchema, overheadCents: centsSchema,
    knownFinanceCostCents: centsSchema, knownFinanceContributionCents: signedCentsSchema,
});
export const reportingExecutionTotalsSchema = z.strictObject({
    invoicedCents: centsSchema, collectedCents: centsSchema, writtenOffCents: centsSchema,
    outstandingCents: centsSchema, approvedDirectExpenseCents: centsSchema,
    referralCommissionCents: centsSchema, overheadCents: centsSchema,
    knownFinanceCostCents: centsSchema, knownFinanceContributionCents: signedCentsSchema,
});
export const reportingSourceStateSchema = z.discriminatedUnion('state', [
    z.strictObject({ state: z.literal('present') }),
    z.strictObject({ state: z.literal('unavailable'), reasonCode: reportingSourceUnavailableReasonSchema }),
    z.strictObject({ state: z.literal('not-requested') }),
]);
const financeTotalFields = [
    'invoicedCents', 'collectedCents', 'writtenOffCents', 'outstandingCents',
    'approvedDirectExpenseCents', 'referralCommissionCents', 'overheadCents',
    'knownFinanceCostCents', 'knownFinanceContributionCents',
];
const financeExecutionDataSchema = z.strictObject({
    executionId: z.string().uuid(), datasetKey: z.literal('finance-project-performance'),
    definitionName: z.string().min(1).max(160), definitionVersionId: z.string().uuid().nullable(),
    definition: reportingFinanceProjectPerformanceDefinitionValueSchema,
    state: z.enum(['present', 'unavailable']),
    unavailableReason: reportingExecutionUnavailableReasonSchema.nullable(),
    sourceStates: z.strictObject({ projects: reportingSourceStateSchema, finance: reportingSourceStateSchema }),
    asOf: z.string().datetime({ offset: true }), totalProjects: z.number().int().nonnegative(),
    returned: z.number().int().nonnegative(), truncated: z.boolean(),
    rows: z.array(reportingFinanceProjectPerformanceRowSchema).max(500),
    totals: reportingExecutionTotalsSchema.nullable(),
}).superRefine((data, context) => {
    if (data.returned !== data.rows.length ||
        new Set(data.rows.map(({ projectId }) => projectId)).size !== data.rows.length) {
        context.addIssue({ code: 'custom', path: ['returned'], message: 'Finance result rows must reconcile.' });
    }
    if (data.state === 'present') {
        if (data.unavailableReason !== null || data.totals === null ||
            data.sourceStates.projects.state !== 'present' || data.sourceStates.finance.state !== 'present' ||
            data.totalProjects < data.rows.length || data.rows.length > data.definition.rowLimit) {
            context.addIssue({ code: 'custom', path: ['state'], message: 'Present Finance result is inconsistent.' });
            return;
        }
        for (const field of financeTotalFields) {
            if (data.totals[field] !== data.rows.reduce((sum, row) => sum + row[field], 0)) {
                context.addIssue({ code: 'custom', path: ['totals', field], message: 'Finance totals must reconcile.' });
            }
        }
    }
    else {
        validateUnavailable(data, context);
    }
});
const trainingTotalFields = [
    'assignmentTotal', 'assignmentOpen', 'assignmentCompleted', 'assignmentWaived',
    'assignmentOverdue', 'completedCourseCount', 'assessmentAttemptCount',
    'assessmentPassedCount', 'activitySessionCount', 'activityActiveSeconds',
];
const trainingExecutionDataSchema = z.strictObject({
    executionId: z.string().uuid(), datasetKey: z.literal('training-team-summary'),
    definitionName: z.string().min(1).max(160), definitionVersionId: z.string().uuid().nullable(),
    definition: reportingTrainingTeamSummaryDefinitionValueSchema,
    state: z.enum(['present', 'unavailable']),
    unavailableReason: reportingExecutionUnavailableReasonSchema.nullable(),
    sourceStates: z.strictObject({ directory: reportingSourceStateSchema, training: reportingSourceStateSchema }),
    asOf: z.string().datetime({ offset: true }), totalMemberships: z.number().int().nonnegative(),
    returned: z.number().int().nonnegative(), truncated: z.boolean(),
    activityBasis: z.literal('supporting-training-activity'), payableTime: z.literal(false),
    rows: z.array(reportingTrainingTeamSummaryRowSchema).max(500),
    totals: reportingTrainingTeamSummaryTotalsSchema.nullable(),
}).superRefine((data, context) => {
    if (data.returned !== data.rows.length ||
        new Set(data.rows.map(({ membershipId }) => membershipId)).size !== data.rows.length) {
        context.addIssue({ code: 'custom', path: ['returned'], message: 'Training result rows must reconcile.' });
    }
    if (data.state === 'present') {
        if (data.unavailableReason !== null || data.totals === null ||
            data.sourceStates.directory.state !== 'present' || data.sourceStates.training.state !== 'present' ||
            data.totalMemberships < data.rows.length || data.rows.length > data.definition.rowLimit) {
            context.addIssue({ code: 'custom', path: ['state'], message: 'Present Training result is inconsistent.' });
            return;
        }
        for (const field of trainingTotalFields) {
            if (data.totals[field] !== data.rows.reduce((sum, row) => sum + row[field], 0)) {
                context.addIssue({ code: 'custom', path: ['totals', field], message: 'Training totals must reconcile.' });
            }
        }
    }
    else {
        validateUnavailable(data, context);
    }
});
function validateUnavailable(data, context) {
    if (data.unavailableReason === null || data.totals !== null || data.returned !== 0 || data.rows.length !== 0) {
        context.addIssue({ code: 'custom', path: ['state'], message: 'Unavailable result cannot contain data.' });
        return;
    }
    const providerReason = reportingSourceUnavailableReasonSchema.safeParse(data.unavailableReason);
    if (providerReason.success) {
        if (!Object.values(data.sourceStates).some((source) => source.state === 'unavailable' && source.reasonCode === providerReason.data)) {
            context.addIssue({ code: 'custom', path: ['sourceStates'], message: 'Unavailable owner is not identified.' });
        }
    }
    else if (Object.values(data.sourceStates).some(({ state }) => state !== 'not-requested')) {
        context.addIssue({ code: 'custom', path: ['sourceStates'], message: 'Pre-source failure requested an owner.' });
    }
}
export const reportingExecutionResponseSchema = z.strictObject({
    data: z.discriminatedUnion('datasetKey', [financeExecutionDataSchema, trainingExecutionDataSchema]),
});
//# sourceMappingURL=reporting-finance-project-performance.contract.js.map